import React, { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import { currentWork, type CurrentWorkItem } from "../data/currentWork";
import { EYEBROW, H3 } from "../utils/typography";
type CurrentlyWorkingOnProps = {
  /** Seconds to wait before entering, so it follows the hero headline */
  delay?: number;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function CurrentlyWorkingOn({
  delay = 0
}: CurrentlyWorkingOnProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, {
    margin: "-10% 0px"
  });
  const animate = !reduce && inView;
  return <motion.section ref={ref} aria-labelledby="now-heading" className="mt-20 md:mt-28" initial={reduce ? false : {
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease,
    delay
  }} data-id="element-192" data-mp="2sr6ncat0fljg">
      <h2 id="now-heading" className={`${EYEBROW} flex items-center gap-3 text-rust`} data-id="element-193" data-mp="12scagq31n0iq">
        <span className="relative flex h-2.5 w-2.5" aria-hidden="true" data-id="element-194" data-mp="17r5l8aqy4df7">
          {!reduce && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mustard opacity-70" data-id="element-195" data-mp="tredusuxpva6" />}
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-mustard" data-id="element-196" data-mp="xs83prrxtk5p" />
        </span>
        Currently working on
      </h2>

      <ul className="mt-6 grid grid-cols-1 gap-6 md:mt-8 md:grid-cols-2 xl:grid-cols-3" data-id="element-197" data-mp="k0a17zsqemii">
        {currentWork.map((item, i) => <motion.li key={item.id} className="flex" initial={reduce ? false : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease,
        delay: delay + 0.08 + i * 0.06
      }} data-id="element-198" data-mp="a3ezl815w600" data-mpi={i}>
            <WorkItem item={item} animate={animate} offset={i * 0.9} data-id="element-199" data-mp="14tnnvq73p87" />
          </motion.li>)}
      </ul>

      <a href="#contact" className="group mt-6 inline-flex items-center gap-2 text-sm text-espresso/70 underline decoration-espresso/25 underline-offset-4 transition-colors duration-150 ease-out hover:text-rust hover:decoration-rust" data-id="element-200" data-mp="63mq8t3y1vtl">
        Curious about any of these? Ask me about them
        <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-201" data-mp="1tzuldjyzghjy" />
      </a>
    </motion.section>;
}
function WorkItem({
  item,
  animate,
  offset
}: {
  item: CurrentWorkItem;
  animate: boolean;
  offset: number;
}) {
  return <article className="flex w-full flex-col rounded-3xl bg-paper p-6 md:p-8" data-id="element-202" data-mp="2hodt9ye4esbi">
      <p className={`${EYEBROW} text-espresso/70`} data-id="element-203" data-mp="2cqc91a4ykmyg">
        {item.org}
        {item.role && <>
            {" "}
            <span aria-hidden="true" data-id="element-204" data-mp="16b1onlaje5oa">·</span> {item.role}
          </>}
      </p>
      <h3 className={`mt-3 ${H3} text-espresso`} data-id="element-205" data-mp="11anlohnsllx4">{item.title}</h3>
      <p className="mt-3 max-w-md text-base leading-relaxed text-espresso/80" data-id="element-206" data-mp="3cy91csaqqyxw">{item.summary}</p>

      <div className="mt-auto pt-8" data-id="element-207" data-mp="3hxd8lde3umll">
        <p className={`${EYEBROW} text-espresso/70`} data-id="element-208" data-mp="3hyevqg8eazjo">{item.label}</p>
        <div className="mt-3" data-id="element-209" data-mp="3lz75rbp3d8h0">
          {item.visual === "questions" && <RotatingQuestion items={item.items} animate={animate} offset={offset} data-id="element-210" data-mp="3lxnpvm7ckzfr" />}
          {item.visual === "cycle" && <SprintCycle items={item.items} animate={animate} offset={offset} data-id="element-211" data-mp="3qrrtpwlbgrcm" />}
          {item.visual === "flow" && <DataFlow items={item.items} animate={animate} data-id="element-212" data-mp="3vr4gkhc6tyf7" />}
        </div>
      </div>
    </article>;
}

/** Cycles index every `interval` ms while animating; starts after `offset` seconds. */
function useCycle(length: number, animate: boolean, offset: number, interval: number) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!animate) return;
    let timer: number | undefined;
    const start = window.setTimeout(() => {
      timer = window.setInterval(() => setIndex(i => (i + 1) % length), interval);
    }, offset * 1000);
    return () => {
      window.clearTimeout(start);
      if (timer) window.clearInterval(timer);
    };
  }, [animate, length, offset, interval]);
  return index;
}
function RotatingQuestion({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  const index = useCycle(items.length, animate, offset, 3200);
  return <div className="rounded-2xl bg-espresso px-5 py-4" data-id="element-213" data-mp="119u59h2bi1vg">
      {/* Screen readers get the full list once, not a moving target */}
      <ul className="sr-only" data-id="element-214" data-mp="wbiwl9nrdxet">
        {items.map(q => <li key={q} data-id="element-215" data-mp="w8wq7dcltny6">{q}</li>)}
      </ul>
      <div aria-hidden="true" className="relative h-14 overflow-hidden" data-id="element-216" data-mp="1b0fc1rs5acpg">
        <AnimatePresence initial={false} mode="popLayout" data-id="element-217" data-mp="168f4bd5u2fib">
          <motion.p key={items[index]} className="absolute inset-x-0 top-0 text-base font-medium leading-snug text-cream" initial={{
          y: "100%",
          opacity: 0
        }} animate={{
          y: "0%",
          opacity: 1
        }} exit={{
          y: "-100%",
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease
        }} data-id="element-218" data-mp="166ubbrz634mk">
            {items[index]}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>;
}
function SprintCycle({
  items,
  animate,
  offset
}: {
  items: string[];
  animate: boolean;
  offset: number;
}) {
  const index = useCycle(items.length, animate, offset, 1600);
  const layoutId = useId();
  return <ol className="grid grid-cols-2 gap-2" data-id="element-219" data-mp="37z5vlxq838ij">
      {items.map((step, i) => {
      const active = animate && i === index;
      return <li key={step} className="relative" data-id="element-220" data-mp="3cv397wa3q0rm" data-mpi={i}>
            {active && <motion.span layoutId={layoutId} aria-hidden="true" className="absolute inset-0 rounded-full bg-espresso" transition={{
          duration: 0.25,
          ease
        }} data-id="element-221" data-mp="2ywwl37uyff55" />}
            <span className={`relative flex h-10 items-center justify-center rounded-full text-sm font-medium uppercase tracking-wide ring-1 transition-colors duration-200 ease-out ${active ? "text-cream ring-transparent" : "text-espresso ring-espresso/20"}`} data-id="element-222" data-mp="1sh2siu0opnu5">
              {step}
            </span>
          </li>;
    })}
    </ol>;
}

/** Three nodes joined by tracks; a mustard pulse carries data from GA4 through the MCP to Claude. */
function DataFlow({
  items,
  animate
}: {
  items: string[];
  animate: boolean;
}) {
  return <div className="flex h-[5.5rem] items-center rounded-2xl bg-espresso px-4" data-id="element-223" data-mp="36h76p8w7q6di">
      <p className="sr-only" data-id="element-224" data-mp="36ja4if3lx3ss">{items.join(" connects through ")}</p>
      <div aria-hidden="true" className="flex w-full items-center" data-id="element-225" data-mp="31icoo962klug">
        {items.map((node, i) => <React.Fragment key={node} data-id="element-226" data-mp="2xi2ai984ip4i" data-mpi={i}>
            <span className={`flex h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-full px-3 text-sm sm:px-4 font-medium uppercase tracking-wide ${i === 1 ? "bg-mustard text-espresso" : "text-cream ring-1 ring-cream/25"}`} data-id="element-227" data-mp="zq7sglhjf2dx">
              {node}
            </span>
            {i < items.length - 1 && <span className="relative mx-2 h-1.5 flex-1 overflow-hidden" data-id="element-228" data-mp="2ij9cra8tg4r2">
                <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-cream/20" data-id="element-229" data-mp="2nd40corjl3vf" />
                {animate && <motion.span className="absolute top-1/2 h-1.5 w-6 -translate-y-1/2 rounded-full bg-mustard" initial={{
            left: "-25%"
          }} animate={{
            left: "110%"
          }} transition={{
            duration: 0.9,
            ease: "linear",
            repeat: Infinity,
            repeatDelay: 0.9,
            delay: i * 0.9
          }} data-id="element-230" data-mp="1unn3neww30te" />}
              </span>}
          </React.Fragment>)}
      </div>
    </div>;
}